/**
 * FilterBar — Search + severity filter + sort + density toggle.
 *
 * UX Spec Section 5.2.11 / Story 1.4 AC#6.
 *
 * Fuzzy search with 300ms debounce, severity badge counts,
 * sort by score/name/columns/rows, grid/list density toggle.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Grid3X3, List, Search } from "lucide-react";
import type { SeverityLevel } from "./ScoreHero";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SortField = "score" | "name" | "columns" | "rows";
export type SortDirection = "asc" | "desc";
export type DensityMode = "grid" | "list";

export interface SeverityCounts {
  excellent: number;
  good: number;
  moderate: number;
  poor: number;
  warning: number;
  critical: number;
}

export interface FilterBarProps {
  severityCounts: SeverityCounts;
  activeSeverities: SeverityLevel[];
  onSeverityToggle: (severity: SeverityLevel) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortField: SortField;
  sortDirection: SortDirection;
  onSortChange: (field: SortField, direction: SortDirection) => void;
  density: DensityMode;
  onDensityChange: (mode: DensityMode) => void;
  visibleCount: number;
  totalCount: number;
}

// ---------------------------------------------------------------------------
// Severity badge config
// ---------------------------------------------------------------------------

const SEVERITY_BADGES: {
  key: SeverityLevel;
  icon: string;
  color: string;
  label: string;
  tip: string;
}[] = [
  {
    key: "excellent",
    icon: "■",
    color: "var(--readiness-excellent)",
    label: "Excellent",
    tip: "Excellent — quality score 90–100. Table meets all completeness, uniqueness, consistency, and validity checks.",
  },
  {
    key: "good",
    icon: "✓",
    color: "var(--readiness-good)",
    label: "Good",
    tip: "Good — quality score 75–89. Minor issues (e.g. a few nulls, slight format drift). Safe to proceed with light review.",
  },
  {
    key: "moderate",
    icon: "▲",
    color: "var(--readiness-moderate)",
    label: "Moderate",
    tip: "Moderate — quality score 60–74. Notable problems (significant nulls, cardinality oddities, constraint risks). Needs remediation before downstream use.",
  },
  {
    key: "poor",
    icon: "◆",
    color: "var(--readiness-poor)",
    label: "Poor",
    tip: "Poor — quality score 40–59. Serious structural or completeness issues. Requires significant remediation effort.",
  },
  {
    key: "critical",
    icon: "●",
    color: "var(--readiness-critical)",
    label: "Critical",
    tip: "Critical — quality score under 40. Severe issues (100% null columns, broken PK candidates, type violations). Block downstream use until fixed.",
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FilterBar({
  severityCounts,
  activeSeverities,
  onSeverityToggle,
  searchQuery,
  onSearchChange,
  sortField,
  sortDirection,
  onSortChange,
  density,
  onDensityChange,
  visibleCount,
  totalCount,
}: FilterBarProps) {
  const [localQuery, setLocalQuery] = useState(searchQuery);
  // @ts-expect-error mypy cleanup — runtime contract is correct, type model needs follow-up
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const handleSearch = useCallback(
    (value: string) => {
      setLocalQuery(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => onSearchChange(value), 300);
    },
    [onSearchChange],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSortToggle = (field: SortField) => {
    if (sortField === field) {
      onSortChange(field, sortDirection === "asc" ? "desc" : "asc");
    } else {
      onSortChange(field, field === "name" ? "asc" : "asc");
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted-foreground)]" />
          <input
            type="text"
            value={localQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search tables or columns..."
            className="w-full pl-9 pr-3 py-2 rounded-lg glass-surface-input text-body text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] outline-none focus:ring-1 focus:ring-[var(--ring)]"
          />
        </div>

        {/* Severity filter badges — hover explains each tier */}
        <div className="flex items-center gap-1">
          {SEVERITY_BADGES.map(({ key, icon, color, label, tip }) => {
            const isActive = activeSeverities.includes(key);
            const count = severityCounts[key];
            return (
              <button
                key={key}
                onClick={() => onSeverityToggle(key)}
                aria-pressed={isActive}
                aria-label={`${label}: ${count} table${count === 1 ? "" : "s"}. ${tip}`}
                title={`${label} (${count}) — ${tip}`}
                className={`
                  px-2 py-1 rounded-md text-caption font-medium transition-colors
                  ${
                    isActive
                      ? "ring-1 ring-current"
                      : "opacity-50 hover:opacity-80"
                  }
                `}
                style={{ color }}
              >
                {icon}
                {count}
              </button>
            );
          })}
        </div>

        {/* Sort */}
        <div className="flex items-center gap-1">
          {(["score", "name", "columns", "rows"] as SortField[]).map(
            (field) => (
              <button
                key={field}
                onClick={() => handleSortToggle(field)}
                className={`
                px-2 py-1 rounded-md text-caption transition-colors
                ${
                  sortField === field
                    ? "text-[var(--foreground)] bg-white/10"
                    : "text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                }
              `}
              >
                {field.charAt(0).toUpperCase() + field.slice(1)}
                {sortField === field && (sortDirection === "asc" ? " ↑" : " ↓")}
              </button>
            ),
          )}
        </div>

        {/* Density toggle */}
        <div className="flex items-center gap-1 ml-auto">
          <button
            onClick={() => onDensityChange("grid")}
            aria-pressed={density === "grid"}
            className={`p-1.5 rounded-md transition-colors ${
              density === "grid"
                ? "text-[var(--foreground)] bg-white/10"
                : "text-[var(--muted-foreground)]"
            }`}
            aria-label="Grid view"
          >
            <Grid3X3 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDensityChange("list")}
            aria-pressed={density === "list"}
            className={`p-1.5 rounded-md transition-colors ${
              density === "list"
                ? "text-[var(--foreground)] bg-white/10"
                : "text-[var(--muted-foreground)]"
            }`}
            aria-label="List view"
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Result count — ARIA live region */}
      <div
        aria-live="polite"
        className="text-caption text-[var(--muted-foreground)]"
      >
        Showing {visibleCount} of {totalCount} tables
      </div>
    </div>
  );
}
